// src/ui/GuestCard.jsx — what a temporary chat is, said once, at the top of it.
//
// 🟥 IT IS PART OF THE THREAD, NOT A FLOATING PANEL. The phone's FirstContactCard
//    is rendered as a SIBLING of the message list, so it hovers over the
//    conversation and covers it. This is the first row of the list: it scrolls
//    with the messages, nothing is hidden behind it, and it needs no dismiss
//    button because it is not in anyone's way.
//
// 🟥 AND IT IS NOT A MESSAGE. That is the other half of the requirement —
//    "Clear chat" deletes message documents, and this has to survive that. So it
//    is rendered from the CHAT document plus the link row, which clearing does
//    not touch. Nothing to re-create, nothing to restore.
//
// 🟢 THE PASSCODE IS SHOWN ONLY WHILE THE LINK IS UNCLAIMED. The server keeps the
//    plaintext exactly that long and deletes it at /join (see the note in
//    server/functions/guestLinks.js): while you are still trying to pass the code
//    on, you can read it here; once they are in it has no further use and stops
//    existing. `hasPasscode` outlives it, so the card can still say a code was
//    required without being able to reveal it.
//
// No Block button, deliberately. Blocking is for an account that can come back;
// a guest is one link with an expiry on it, and the honest control is "End now"
// in the link manager — which deletes the conversation rather than hiding it.
import React from 'react';
import { Lock, Timer, Link as LinkIcon, DocText } from './icons';

const fmtDate = (ms) => {
  if (!ms) return null;
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return null; }
};

const fmtLeft = (ms) => {
  const left = (ms || 0) - Date.now();
  if (left <= 0) return 'ended';
  const d = Math.floor(left / 86400000);
  if (d >= 1) return `${d} day${d === 1 ? '' : 's'} left`;
  const h = Math.floor(left / 3600000);
  if (h >= 1) return `${h} hour${h === 1 ? '' : 's'} left`;
  return `${Math.max(1, Math.floor(left / 60000))} min left`;
};

/**
 * @param chat  the chat document (isGuest, expiresAt, guestUid)
 * @param link  the matching guestLinks row, when the viewer owns it — carries
 *              createdAt, note, hasPasscode and (while unclaimed) passcodePlain.
 *              Absent for the guest's own view, which is correct: the guest has
 *              no business reading the link record.
 * @param who   the other person's display name
 * @param mine  true when the viewer is the owner (the one who made the link)
 */
export default function GuestCard({ chat, link, who, mine = true }) {
  if (!chat) return null;
  const expiresAt = chat.expiresAt || link?.expiresAt || 0;
  const over = !expiresAt || expiresAt <= Date.now();
  const made = fmtDate(link?.createdAt);
  const passcode = link?.passcodePlain || null;

  return (
    <div className={`gc-card ${over ? 'over' : ''}`}>
      <div className="gc-head">
        <span className="gc-ico"><Timer width={16} height={16} /></span>
        <div className="gc-head-txt">
          <b>Temporary chat{who ? ` with ${who}` : ''}</b>
          <i>{over ? 'This link has ended' : fmtLeft(expiresAt)}</i>
        </div>
      </div>

      <div className="gc-rows">
        {made && (
          <div className="gc-row">
            <LinkIcon width={14} height={14} />
            <span>Link created <b>{made}</b></span>
          </div>
        )}

        {link?.note && (
          <div className="gc-row">
            <DocText width={14} height={14} />
            <span>For <b>{link.note}</b></span>
          </div>
        )}

        {mine && link?.hasPasscode && (
          <div className="gc-row">
            <Lock width={14} height={14} />
            {passcode
              ? <span>Passcode <b className="gc-code">{passcode}</b></span>
              /* claimed → the plaintext is gone on purpose, and saying so is
                 better than showing a blank where a code used to be */
              : <span>Passcode was required <i>(no longer shown — they have joined)</i></span>}
          </div>
        )}

        <div className="gc-row">
          <Lock width={14} height={14} />
          <span>
            End-to-end encrypted. Everything here — messages, photos, files — is
            deleted when the link {over ? 'ended' : 'expires'}.
          </span>
        </div>
      </div>
    </div>
  );
}
